import React, { useEffect, useState, useMemo } from 'react';
import showPopup from '../utils/showPopup';
import { parseJSONSafe, getItemJSON, setItemJSON, removeItem, clearAllSession } from '../utils/storage';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

function AdminDashboard(props) {
    const { setUser } = props;

    const { t } = useTranslation();
    const navigate = useNavigate();
    
    // Date range options
    const DATE_RANGE_OPTIONS = [
        { value: 'all', label: 'All Time' },
        { value: 'last-week', label: 'Last Week' },
        { value: '15-days', label: 'Last 15 Days' },
        { value: 'last-month', label: 'Last Month' },
        { value: 'custom', label: 'Custom Date Range' }
    ];
    const [selectedProfile, setSelectedProfile] = useState(null);
    const getPageSize = () => {
        if (window.innerWidth <= 768) return 4; // Mobile view
        return 6; // Desktop and tablet view
    };
    const [pageSize, setPageSize] = useState(getPageSize());
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [employers, setEmployers] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [previewFile, setPreviewFile] = useState(null); // { url, label }
    const [dbStatus, setDbStatus] = useState('unknown'); // 'connected', 'disconnected', 'unknown'
    const [activeTab, setActiveTab] = useState('employers');
    const [candidatePage, setCandidatePage] = useState(1);
    const [employerPage, setEmployerPage] = useState(1);
    const [candidatePageSize, setCandidatePageSize] = useState(getPageSize());
    const [employerPageSize, setEmployerPageSize] = useState(getPageSize());

    // --- NEW: Employer filter state ---
    const [employerFilters, setEmployerFilters] = useState({
        companyName: '',
        place: '',
        customRange: 'all'
    });

    // --- NEW: Candidate filter state ---
    const [candidateFilters, setCandidateFilters] = useState({
        candidateName: '',
        tgIdNumber: '',
        place: '',
        customRange: 'all'
    });

    const [isCandidateSearchApplied, setIsCandidateSearchApplied] = useState(false);
    const [isEmployerSearchApplied, setIsEmployerSearchApplied] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [expandedMenu, setExpandedMenu] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    // Denial remark modal state
    const [showRemarkModal, setShowRemarkModal] = useState(false);
    const [remarkText, setRemarkText] = useState('');
    const [remarkContext, setRemarkContext] = useState(null); // { id, isCandidate, profileData }
    
    // Password reset functionality state
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const [passwordResetProfile, setPasswordResetProfile] = useState(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [editedProfile, setEditedProfile] = useState({
        name: '',
        mobile: '',
        email: ''
    });

    // Notification management state
    const [notifications, setNotifications] = useState([]);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [editingNotificationId, setEditingNotificationId] = useState(null);
    const [notificationLoading, setNotificationLoading] = useState(false);

    // Media Management state
    const [mediaItems, setMediaItems] = useState([]);
    const [mediaType, setMediaType] = useState('photo'); // 'photo' or 'video'
    const [mediaTitle, setMediaTitle] = useState('');
    const [mediaDescription, setMediaDescription] = useState('');
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreview, setMediaPreview] = useState(null);
    const [mediaLoading, setMediaLoading] = useState(false);
    const [editingMediaId, setEditingMediaId] = useState(null);
    const [currentPhotoPage, setCurrentPhotoPage] = useState(1);
    const [currentVideoPage, setCurrentVideoPage] = useState(1);
    const mediaItemsPerPage = 2;

    // Split media items into photos and videos for separate tables
    const photoItems = useMemo(() => (Array.isArray(mediaItems) ? mediaItems.filter(m => m && m.type === 'photo') : []), [mediaItems]);
    const videoItems = useMemo(() => (Array.isArray(mediaItems) ? mediaItems.filter(m => m && m.type === 'video') : []), [mediaItems]);

    // Which media view to show: 'images' or 'videos'
    const [mediaView, setMediaView] = useState('images');

    // Filters for photo and video sections
    const [photoFilter, setPhotoFilter] = useState({ title: '', status: 'all' });
    const [videoFilter, setVideoFilter] = useState({ title: '', status: 'all' });

    // Derived filtered lists
    const filteredPhotoItems = useMemo(() => {
        let list = photoItems;
        if (photoFilter.title && photoFilter.title.trim() !== '') {
            const q = photoFilter.title.toLowerCase();
            list = list.filter(m => (m.title || '').toLowerCase().includes(q));
        }
        if (photoFilter.status === 'active') list = list.filter(m => m.is_active);
        else if (photoFilter.status === 'inactive') list = list.filter(m => !m.is_active);
        return list;
    }, [photoItems, photoFilter]);

    const filteredVideoItems = useMemo(() => {
        let list = videoItems;
        if (videoFilter.title && videoFilter.title.trim() !== '') {
            const q = videoFilter.title.toLowerCase();
            list = list.filter(m => (m.title || '').toLowerCase().includes(q));
        }
        if (videoFilter.status === 'active') list = list.filter(m => m.is_active);
        else if (videoFilter.status === 'inactive') list = list.filter(m => !m.is_active);
        return list;
    }, [videoItems, videoFilter]);

    // Reset page to 1 when filters or view change
    useEffect(() => { setCurrentPhotoPage(1); }, [photoFilter, mediaView]);
    useEffect(() => { setCurrentVideoPage(1); }, [videoFilter, mediaView]);

    const adminUser = useMemo(() => {
            return getItemJSON('adminLoggedIn');
    }, []);

    // Collapse sidebar on tablet/mobile sizes to match Home/Employer dashboards
    useEffect(() => {
        const handleResize = () => {
            const isCompact = window.innerWidth <= 1024; // collapse on tablet and below
            setSidebarCollapsed(isCompact);
            // keep page sizes in sync with viewport
            setPageSize(getPageSize());
            setCandidatePageSize(getPageSize());
            setEmployerPageSize(getPageSize());
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        try { setItemJSON('adminActiveTab', activeTab); } catch {}
    }, [activeTab]);

    useEffect(() => { setCandidatePage(1); }, [candidatePageSize]);
    useEffect(() => { setEmployerPage(1); }, [employerPageSize]);

    useEffect(() => {
        const adminSession = getItemJSON('adminLoggedIn');
        if (!adminSession) {
            navigate('/login');
            return;
        }
        fetchEmployers();
        fetchCandidates();
        if (activeTab === 'notifications') {
            fetchNotifications();
        }
    }, [navigate, activeTab]);

    // Helper function to format date - extract only the date part (YYYY-MM-DD) from ISO timestamps
    const formatDateOnly = (dateString) => {
        if (!dateString) return 'N/A';
        // If it's an ISO timestamp like "2001-06-04T18:30:00.000Z", extract just the date part
        if (typeof dateString === 'string') {
            const datePart = dateString.split('T')[0]; // Gets "2001-06-04" from "2001-06-04T18:30:00.000Z"
            return datePart || dateString;
        }
        return dateString;
    };

    // Helper to extract admin token from localStorage (handles JSON or raw string)
    const getAdminToken = () => {
        const tokenObj = getItemJSON('adminLoggedIn');
        if (!tokenObj) return null;
        if (typeof tokenObj === 'string') return tokenObj;
        return tokenObj.token || null;
    };

    // Handle window resize to update page sizes
    useEffect(() => {
        const handleResize = () => {
            const newPageSize = getPageSize();
            setPageSize(newPageSize);
            setCandidatePageSize(newPageSize);
            setEmployerPageSize(newPageSize);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // --- Overview counts ---
    const candidateCounts = useMemo(() => {
        const list = Array.isArray(candidates) ? candidates : [];
        const total = list.length;
        const approved = list.filter(x => x && x.approval_status === 'approved').length;
        const denied = list.filter(x => x && x.approval_status === 'denied').length;
        const pending = list.filter(x => x && (x.approval_status === 'pending' || !x.approval_status)).length;
        return { total, approved, denied, pending };
    }, [candidates]);

    const employerCounts = useMemo(() => {
        const list = Array.isArray(employers) ? employers : [];
        const total = list.length;
        const approved = list.filter(x => x && x.approval_status === 'approved').length;
        const denied = list.filter(x => x && x.approval_status === 'denied').length;
        const pending = list.filter(x => x && (x.approval_status === 'pending' || !x.approval_status)).length;
        return { total, approved, denied, pending };
    }, [employers]);

    // --- Fetch functions with filters ---
    const fetchCandidates = async (isSearching = null) => {
        try {
            const token = getAdminToken();
            if (!token) {
                console.warn('No admin token found, redirecting to login');
                clearAllSession();
                navigate('/login');
                throw new Error('No authentication token found');
            }

            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            console.log('[Admin] Fetching candidates, headers:', { Authorization: headers.Authorization });

            // Use parameter if provided, otherwise use state
            const shouldApplySearch = isSearching !== null ? isSearching : isCandidateSearchApplied;

            // Construct URL with filters if search is applied
            let url = new URL('https://tstranscareers.in/api/all-candidates');
            if (shouldApplySearch) {
                if (candidateFilters.candidateName) {
                    url.searchParams.append('candidateName', candidateFilters.candidateName);
                }
                if (candidateFilters.tgIdNumber) {
                    url.searchParams.append('tgIdNumber', candidateFilters.tgIdNumber);
                }
                if (candidateFilters.place) {
                    url.searchParams.append('place', candidateFilters.place);
                }
                if (candidateFilters.customRange) {
                    url.searchParams.append('customRange', candidateFilters.customRange);
                }
            }

            const res = await fetch(url, {
                method: 'GET',
                headers,
                credentials: 'same-origin'
            });

            if (!res.ok) {
                if (res.status === 401) {
                    console.warn('Candidates fetch returned 401 - clearing session');
                    clearAllSession();
                    navigate('/login');
                    throw new Error('Session expired. Please login again.');
                }
                
                if (res.status === 503) {
                    console.warn('Database service unavailable');
                    const errorData = await res.json().catch(() => ({}));
                    setCandidates([]);
                    setDbStatus('disconnected');
                    return;
                }
                
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            const data = await res.json();
            if (!Array.isArray(data)) {
                console.warn('Expected array but received:', typeof data);
                setCandidates([]);
                return;
            }

            setCandidates(data);
            setDbStatus('connected');
        } catch (error) {
            console.error('Error fetching candidates:', error);
            setCandidates([]);
            showPopup('Failed to load candidates: ' + error.message, 'Error', { type: 'error' });
        }
    };

    const fetchEmployers = async (isSearching = null) => {
        // Use parameter if provided, otherwise use state
        const shouldApplySearch = isSearching !== null ? isSearching : isEmployerSearchApplied;
        
        let url = new URL('https://tstranscareers.in/api/all-employers');
        
        // Only apply filters if search is active
        if (shouldApplySearch) {
            if (employerFilters.companyName) url.searchParams.append('companyName', employerFilters.companyName);
            if (employerFilters.place) url.searchParams.append('place', employerFilters.place);
            
            // Handle date range
            if (employerFilters.customRange !== 'all') {
                if (employerFilters.customRange === 'custom') {
                    if (employerFilters.fromDate) url.searchParams.append('fromDate', employerFilters.fromDate);
                    if (employerFilters.toDate) url.searchParams.append('toDate', employerFilters.toDate);
                } else {
                    url.searchParams.append('dateRange', employerFilters.customRange);
                }
            }
        }

        try {
            const token = getAdminToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            console.log('[Admin] Fetching employers from', url.toString(), 'Authorization:', !!headers.Authorization);

            const res = await fetch(url, {
                method: 'GET',
                headers,
                credentials: 'same-origin'
            });
            if (!res.ok) {
                if (res.status === 401) {
                    console.warn('Employers fetch returned 401 - clearing session');
                    clearAllSession();
                    navigate('/login');
                    throw new Error('Session expired. Please login again.');
                }
                
                if (res.status === 503) {
                    console.warn('Database service unavailable');
                    const errorData = await res.json().catch(() => ({}));
                    setEmployers([]);
                    setDbStatus('disconnected');
                    return;
                }
                
                console.error('Fetch error:', {
                    status: res.status,
                    statusText: res.statusText
                });
                const errorText = await res.text();
                console.error('Error response:', errorText);
                throw new Error(`HTTP error! status: ${res.status}`);
            }
            const data = await res.json();
            console.log('Received employer data:', data);
            if (!Array.isArray(data)) {
                console.warn('Expected array of employers but received:', typeof data);
                setEmployers([]);
                return;
            }
            setEmployers(data);
            setDbStatus('connected');
        } catch (error) {
            console.error('Error fetching employers:', error);
            setEmployers([]);
        }
    };

    const handleLogout = () => {
        clearAllSession();
        setUser(null);
        navigate('/login');
    };

    const employerColumns = useMemo(() => ([
        { key: 'company_name', label: t('companyName') },
        { key: 'brand_name', label: t('brandName') },
        { key: 'sector', label: t('sector') },
        { key: 'website', label: t('website') },
        { key: 'gst_number', label: t('gstNumber') },
        { key: 'description', label: t('description') },
        { key: 'has_transgender_policy', label: t('hasTransPolicy') },
        { key: 'open_to_accommodation', label: t('openToAccommodation') },
        { key: 'district', label: t('district') },
        { key: 'mandal', label: t('mandal') },
        { key: 'city', label: t('city') },
        { key: 'pincode', label: t('pincode') },
        { key: 'contact_person_name', label: t('contactPerson') },
        { key: 'department', label: t('department') },
        { key: 'designation', label: t('designation') },
        { key: 'mobile_number', label: t('mobile') },
        { key: 'alternative_mobile', label: t('altMobile') },
        { key: 'email_id', label: t('email') },
        { key: 'login_email', label: t('loginEmail') },
        { key: 'number_of_employees', label: t('employees') },
        { key: 'registration_certificate', label: 'Registration Certificate' },
        { key: 'annual_report', label: 'Annual Report' },
        { key: 'transgender_policy_certificate', label: 'Transgender Persons Policy Certificate' },
        { key: 'company_logo', label: 'Company Logo' },
    ]), [t]);

    const defaultCandidateColumns = useMemo(() => ([
        { key: 'candidateName', label: t('candidateName') },
        { key: 'tgIdNumber', label: 'TG ID Number' },
        { key: 'emailId', label: t('email') },
        { key: 'mobileNumber', label: t('mobile') },
        { key: 'educationalQualification', label: t('qualification') },
        { key: 'gender', label: t('gender') },
        { key: 'dateOfBirth', label: t('dob') },
        { key: 'district', label: t('district') },
        { key: 'mandal', label: t('mandal') },
        { key: 'city', label: t('city') },
        { key: 'pincode', label: t('pincode') },
        { key: 'preferredJobLocation', label: t('preferredJobLocation') },
        { key: 'preferredJobSector', label: t('preferredJobSector') },
        { key: 'skills', label: t('skills') },
        { key: 'tgIdCard', label: 'TG ID Card' },
        { key: 'addressIdProof', label: 'Address ID Proof' },
    ]), [t]);

    const candidateColumns = useMemo(() => {
        if (!candidates || candidates.length === 0) return defaultCandidateColumns;
        const sample = candidates.find(c => c && typeof c === 'object') || {};
        const hasAnyDefaultValue = defaultCandidateColumns.some(col => sample[col.key] !== undefined && sample[col.key] !== null && sample[col.key] !== '');
        if (hasAnyDefaultValue) return defaultCandidateColumns;
        return Object.keys(sample).map(k => ({ key: k, label: k.replace(/_/g, ' ') }));
    }, [candidates, defaultCandidateColumns]);

    const formatCellValue = (value, key) => {
        if (!value) return '-';
        const fileFields = [
            'tgIdCard', 'addressIdProof',
            'registration_certificate', 'annual_report', 'transgender_policy_certificate', 'company_logo'
        ];
        if (fileFields.includes(key)) {
                return (
                    <button
                        className="btn view-btn"
                        onClick={() => setPreviewFile({ url: `https://tstranscareers.in/api/uploads/${value}`, label: key })}
                    >
                        View
                    </button>
                );
        }
        return String(value);
    };

    const renderTable = (rows, columns, emptyText, type) => {
        if (!rows || rows.length === 0) {
            return <div className="empty">{emptyText}</div>;
        }
        return (
            <div className="table-wrapper">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Status</th>
                            {columns.map(col => (
                                <th key={col.key}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => (
                            <tr key={idx}>
                                                                <td>
                                                                    <div className="status-dropdown">
                                                                        <button className="status-btn">
                                                                            {(row.approval_status || '').charAt(0).toUpperCase() + (row.approval_status || '').slice(1)} ▼
                                                                        </button>
                                                                        {row.approval_status === 'pending' && (
                                                                            <div className="status-dropdown-content">
                                                                                {type === 'candidate' ? (
                                                                                    <>
                                                                                        <button onClick={() => updateCandidateStatus(row.id, 'approved')}>Approve</button>
                                                                                        <button onClick={() => updateCandidateStatus(row.id, 'denied')}>Deny</button>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <button onClick={() => updateEmployerStatus(row.id, 'approved')}>Approve</button>
                                                                                        <button onClick={() => updateEmployerStatus(row.id, 'denied')}>Deny</button>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                {columns.map(col => (
                                    <td key={col.key}>
                                        {col.render ? col.render(row) : formatCellValue(row[col.key], col.key)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const pagedCandidates = useMemo(() => {
        const filtered = statusFilter === 'all' 
            ? candidates.filter(c => c && typeof c === 'object')
            : candidates.filter(c => c && typeof c === 'object' && c.approval_status === statusFilter);
        
        const start = (candidatePage - 1) * candidatePageSize;
        return filtered.slice(start, start + candidatePageSize);
    }, [candidates, candidatePage, candidatePageSize, statusFilter]);

    const pagedEmployers = useMemo(() => {
        const list = Array.isArray(employers) ? employers : [];
        const filtered = statusFilter === 'all'
            ? list
            : list.filter(e => e.approval_status === statusFilter);
        
        const start = (employerPage - 1) * employerPageSize;
        return filtered.slice(start, start + employerPageSize);
    }, [employers, employerPage, employerPageSize, statusFilter]);

    const totalCandidatePages = useMemo(() => {
        const filtered = statusFilter === 'all' 
            ? candidates.filter(c => c && typeof c === 'object')
            : candidates.filter(c => c && typeof c === 'object' && c.approval_status === statusFilter);
        return Math.max(1, Math.ceil(filtered.length / candidatePageSize));
    }, [candidates, candidatePageSize, statusFilter]);
    const totalEmployerPages = useMemo(() => {
        const list = Array.isArray(employers) ? employers : [];
        const filtered = statusFilter === 'all'
            ? list
            : list.filter(e => e.approval_status === statusFilter);
        return Math.max(1, Math.ceil(filtered.length / employerPageSize));
    }, [employers, employerPageSize, statusFilter]);

    useEffect(() => { if (candidatePage > totalCandidatePages) setCandidatePage(1); }, [totalCandidatePages, candidatePage]);
    useEffect(() => { if (employerPage > totalEmployerPages) setEmployerPage(1); }, [totalEmployerPages, employerPage]);

    const getCandidateEmptyText = () => {
      if (!isCandidateSearchApplied) return 'No candidate profiles';
      return 'No profile found with the given data';
    };
    const getEmployerEmptyText = () => {
      if (!isEmployerSearchApplied) return 'No employer profiles';
      return 'No profile found with the given data';
    };

    // Pagination component
    const PageSizeAndPagination = ({ page, totalPages, onPageChange }) => (
        <div className="pagination">
            <div className="page-nav">
                <button className="btn" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>{t('prev') || 'Prev'}</button>
                <span className="page-info">{(t('page') || 'Page')} {page} {(t('of') || 'of')} {totalPages}</span>
                <button className="btn" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>{t('next') || 'Next'}</button>
            </div>
        </div>
    );

    // Candidate Search
    const handleCandidateSearch = (e) => {
        e.preventDefault();
        
        // Check if all search fields are empty
        const isEmpty = !candidateFilters.candidateName.trim() && 
                       !candidateFilters.tgIdNumber.trim() && 
                       !candidateFilters.place.trim() && 
                       candidateFilters.customRange === 'all';
        
        if (isEmpty) {
            showPopup('Please enter any details to search', 'Info', { type: 'info' });
            return;
        }
        
        setIsCandidateSearchApplied(true);
        fetchCandidates(true); // Pass true to indicate this is a search
    };

    // Employer Search
    const handleEmployerSearch = (e) => {
        e.preventDefault();
        
        // Check if all search fields are empty
        const isEmpty = !employerFilters.companyName.trim() && 
                       !employerFilters.place.trim() && 
                       employerFilters.customRange === 'all';
        
        if (isEmpty) {
            showPopup('Please enter any details to search', 'Info', { type: 'info' });
            return;
        }
        
        setIsEmployerSearchApplied(true);
        fetchEmployers(true); // Pass true to indicate this is a search
    };

    const handleCandidateClear = () => {
        setCandidateFilters({
            candidateName: '',
            tgIdNumber: '',
            place: '',
            customRange: 'all',
            fromDate: '',
            toDate: '',
        });
        setIsCandidateSearchApplied(false);
        fetchCandidates(false); // Pass false to indicate no search filters
    };

        const updateCandidateStatus = async (id, status) => {
            const token = getAdminToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            
            try {
                console.log(`Updating candidate ${id} status to: ${status}`);
                if (status === 'denied') {
                    // Open modal and defer action
                    setRemarkContext({ id, isCandidate: true, profileData: null });
                    setRemarkText('');
                    setShowRemarkModal(true);
                    return;
                }
                
                const res = await fetch(`https://tstranscareers.in/api/admin/candidate-status/${id}`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ status }),
                });
                
                if (res.status === 401) {
                    console.warn('Update candidate status returned 401');
                    clearAllSession();
                    navigate('/login');
                    return;
                }
                
                const result = await res.json();
                
                if (res.ok) {
                    console.log('✅ Candidate status updated successfully:', result);
                    showPopup(`Candidate ${result.candidateName || 'profile'} has been ${status} successfully!`, 'Success', { type: 'success' });
                    fetchCandidates(); // Refresh list
                } else {
                    console.error('Failed to update candidate status:', result);
                    showPopup(`Error: ${result.message || 'Failed to update candidate status'}`, 'Error', { type: 'error' });
                }
            } catch (e) {
                console.error('Error updating candidate status:', e);
                showPopup('Network error: Failed to update candidate status. Please try again.', 'Error', { type: 'error' });
            }
        };

        const updateEmployerStatus = async (id, status) => {
            const token = getAdminToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            
            try {
                console.log(`Updating employer ${id} status to: ${status}`);
                
                const res = await fetch(`https://tstranscareers.in/api/admin/employer-status/${id}`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ status }),
                });
                
                if (res.status === 401) {
                    console.warn('Update employer status returned 401');
                    clearAllSession();
                    navigate('/login');
                    return;
                }
                
                const result = await res.json();
                
                if (res.ok) {
                    console.log('✅ Employer status updated successfully:', result);
                    showPopup(`Employer ${result.companyName || 'profile'} has been ${status} successfully!`, 'Success', { type: 'success' });
                    fetchEmployers(); // Refresh list
                } else {
                    console.error('Failed to update employer status:', result);
                    showPopup(`Error: ${result.message || 'Failed to update employer status'}`, 'Error', { type: 'error' });
                }
            } catch (e) {
                console.error('Error updating employer status:', e);
                showPopup('Network error: Failed to update employer status. Please try again.', 'Error', { type: 'error' });
            }
        };

    // Add this function to handle status updates
        const updateStatus = async (id, newStatus, profileData = null) => {
        // Determine if it's a candidate or employer from the provided profile or selectedProfile
        const profile = profileData || selectedProfile;
        const isCandidate = profile && profile.hasOwnProperty && profile.hasOwnProperty('candidateName');
        const endpoint = isCandidate ? 
            `https://tstranscareers.in/api/admin/candidate-status/${id}` : 
            `https://tstranscareers.in/api/admin/employer-status/${id}`;

        const token = getAdminToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        try {
            console.log(`Updating ${isCandidate ? 'candidate' : 'employer'} ${id} status to: ${newStatus}`);
            if (newStatus === 'denied') {
                // Open modal and defer action
                setRemarkContext({ id, isCandidate, profileData: profile });
                setRemarkText('');
                setShowRemarkModal(true);
                return;
            }
            
            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({ status: newStatus })
            });

            if (response.status === 401) {
                console.warn('Status update returned 401');
                clearAllSession();
                navigate('/login');
                return;
            }

            const result = await response.json();

            if (response.ok) {
                // Refresh the data after status update
                if (isCandidate) {
                    fetchCandidates();
                } else {
                    fetchEmployers();
                }
                
                // Show success message with profile name
                const profileName = isCandidate 
                    ? (result.candidateName || profile.candidateName || 'candidate') 
                    : (result.companyName || profile.company_name || 'employer');
                    
                showPopup(`${profileName} profile has been ${newStatus} successfully!`, 'Success', { type: 'success' });
                
                // Close the detailed view and go back to list if it was open
                if (selectedProfile) {
                    setSelectedProfile(null);
                }
            } else {
                console.error('Failed to update status:', result);
                showPopup(`Error: ${result.message || 'Failed to update status'}`, 'Error', { type: 'error' });
            }
        } catch (error) {
            console.error('Error updating status:', error);
            showPopup('Network error: Failed to update status. Please try again.', 'Error', { type: 'error' });
        }
    };

    // Submit handler for denial remark modal
    const handleSubmitDenialRemark = async () => {
        if (!remarkContext) return;
        const token = getAdminToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const { id, isCandidate } = remarkContext;
        const endpoint = isCandidate
            ? `https://tstranscareers.in/api/admin/candidate-status/${id}`
            : `https://tstranscareers.in/api/admin/employer-status/${id}`;
        const trimmed = (remarkText || '').trim();
        if (!trimmed) {
            showPopup('Rejection remark is required.', 'Info', { type: 'info' });
            return;
        }
        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({ status: 'denied', remark: trimmed })
            });
            const result = await res.json().catch(() => ({}));
            if (res.status === 401) {
                clearAllSession();
                navigate('/login');
                return;
            }
            if (res.ok) {
                if (isCandidate) {
                    fetchCandidates();
                } else {
                    fetchEmployers();
                }
                setShowRemarkModal(false);
                setRemarkText('');
                setRemarkContext(null);
                showPopup('Profile has been denied successfully.', 'Success', { type: 'success' });
            } else {
                showPopup(result.message || 'Failed to deny profile', 'Error', { type: 'error' });
            }
        } catch (e) {
            console.error('Error denying profile:', e);
            showPopup('Network error: Failed to deny profile.', 'Error', { type: 'error' });
        }
    };

    // Handle password reset functionality
    const handleGeneratePassword = () => {
        if (!selectedProfile) return;
        
        const isCandidate = selectedProfile.hasOwnProperty('candidateName');
        setPasswordResetProfile(selectedProfile);
        setEditedProfile({
            name: isCandidate ? selectedProfile.candidateName : selectedProfile.company_name,
            mobile: isCandidate ? selectedProfile.mobileNumber : selectedProfile.mobile_number,
            email: isCandidate ? selectedProfile.emailId : selectedProfile.email_id
        });
        setShowPasswordResetModal(true);
        setIsEditingProfile(false);
    };

    const handleEditProfile = () => {
        setIsEditingProfile(true);
    };

    const handleCancelEdit = () => {
        setIsEditingProfile(false);
        // Reset to original values
        if (passwordResetProfile) {
            const isCandidate = passwordResetProfile.hasOwnProperty('candidateName');
            setEditedProfile({
                name: isCandidate ? passwordResetProfile.candidateName : passwordResetProfile.company_name,
                mobile: isCandidate ? passwordResetProfile.mobileNumber : passwordResetProfile.mobile_number,
                email: isCandidate ? passwordResetProfile.emailId : passwordResetProfile.email_id
            });
        }
    };

    const handleClosePasswordResetModal = () => {
        setShowPasswordResetModal(false);
        setPasswordResetProfile(null);
        setIsEditingProfile(false);
    };

    // Notification Management Functions
    const fetchNotifications = async () => {
        try {
            const token = getAdminToken();
            const response = await fetch('https://tstranscareers.in/api/api/notifications', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setNotifications(data.notifications);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const handleCreateNotification = async () => {
        if (!notificationMessage.trim()) {
            alert('Please enter a notification message');
            return;
        }

        setNotificationLoading(true);
        try {
            const token = getAdminToken();
            const response = await fetch('https://tstranscareers.in/api/api/notification', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: notificationMessage,
                    is_active: false // Create as inactive by default
                })
            });

            const data = await response.json();
            if (data.success) {
                alert('Notification created successfully!');
                setNotificationMessage('');
                fetchNotifications();
            } else {
                alert('Error creating notification: ' + data.message);
            }
        } catch (error) {
            console.error('Error creating notification:', error);
            alert('Network error. Please try again.');
        } finally {
            setNotificationLoading(false);
        }
    };

    const handleToggleNotification = async (id) => {
        // Check if we're trying to activate and already have 6 active
        const notification = notifications.find(n => n.id === id);
        if (notification && !notification.is_active) {
            const activeCount = notifications.filter(n => n.is_active).length;
            if (activeCount >= 6) {
                alert('Maximum 6 notifications can be active at the same time. Please deactivate one notification before activating another.');
                return;
            }
        }

        setNotificationLoading(true);
        try {
            const token = getAdminToken();
            const response = await fetch(`https://tstranscareers.in/api/api/notification/${id}/toggle`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            if (data.success) {
                fetchNotifications();
            } else {
                alert('Error toggling notification: ' + data.message);
            }
        } catch (error) {
            console.error('Error toggling notification:', error);
            alert('Network error. Please try again.');
        } finally {
            setNotificationLoading(false);
        }
    };

    const handleDeleteNotification = async (id) => {
        if (!window.confirm('Are you sure you want to delete this notification?')) {
            return;
        }

        setNotificationLoading(true);
        try {
            const token = getAdminToken();
            const response = await fetch(`https://tstranscareers.in/api/api/notification/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            if (data.success) {
                alert('Notification deleted successfully!');
                fetchNotifications();
            } else {
                alert('Error deleting notification: ' + data.message);
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
            alert('Network error. Please try again.');
        } finally {
            setNotificationLoading(false);
        }
    };

    const handleUpdateNotification = async (id) => {
        if (!notificationMessage.trim()) {
            alert('Please enter a notification message');
            return;
        }

        setNotificationLoading(true);
        try {
            const token = getAdminToken();
            const notification = notifications.find(n => n.id === id);
            const response = await fetch(`https://tstranscareers.in/api/api/notification/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: notificationMessage,
                    is_active: notification.is_active
                })
            });

            const data = await response.json();
            if (data.success) {
                alert('Notification updated successfully!');
                setNotificationMessage('');
                setEditingNotificationId(null);
                fetchNotifications();
            } else {
                alert('Error updating notification: ' + data.message);
            }
        } catch (error) {
            console.error('Error updating notification:', error);
            alert('Network error. Please try again.');
        } finally {
            setNotificationLoading(false);
        }
    };

    const handleEditNotification = (notification) => {
        setEditingNotificationId(notification.id);
        setNotificationMessage(notification.message);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit_Notification = () => {
        setEditingNotificationId(null);
        setNotificationMessage('');
    };

    // Media Management Functions
    const fetchMediaItems = async () => {
        try {
            const token = getAdminToken();
            const response = await fetch('https://tstranscareers.in/api/api/media', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setMediaItems(data.media || []);
            }
        } catch (error) {
            console.error('Error fetching media:', error);
        }
    };

    const handleMediaFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // File size validation
            const maxSizeMB = mediaType === 'photo' ? 2 : 3;
            const maxSizeBytes = maxSizeMB * 1024 * 1024;
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            
            if (file.size > maxSizeBytes) {
                alert(`File size (${fileSizeMB}MB) exceeds ${maxSizeMB}MB limit. Please choose a smaller file.`);
                e.target.value = ''; // Clear the input
                return;
            }

            // File type validation
            if (mediaType === 'photo' && !file.type.startsWith('image/')) {
                alert('Please select a valid image file (JPG, PNG, GIF, etc.)');
                e.target.value = '';
                return;
            }

            if (mediaType === 'video' && !file.type.startsWith('video/')) {
                alert('Please select a valid video file (MP4, WebM, etc.)');
                e.target.value = '';
                return;
            }

            console.log(`✅ File selected: ${file.name} (${fileSizeMB}MB)`);
            setMediaFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setMediaPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCreateMedia = async () => {
        if (!mediaFile && !editingMediaId) {
            alert('Please select a file');
            return;
        }

        setMediaLoading(true);
        try {
            const token = getAdminToken();
            
            if (!token) {
                alert('Authentication error. Please login again.');
                return;
            }
            
            console.log('🔐 Using admin token for media upload');
            
            const formData = new FormData();
            
            // Auto-generate title from filename if not editing
            const autoTitle = mediaFile ? mediaFile.name.replace(/\.[^/.]+$/, "") : mediaTitle;
            formData.append('title', autoTitle);
            formData.append('description', ''); // Empty description
            formData.append('type', mediaType);
            if (mediaFile) {
                formData.append('file', mediaFile);
            }

            const url = editingMediaId 
                ? `https://tstranscareers.in/api/api/media/${editingMediaId}`
                : 'https://tstranscareers.in/api/api/media';
            
            console.log('📤 Uploading media:', { url, type: mediaType, fileName: mediaFile?.name });
            
            const response = await fetch(url, {
                method: editingMediaId ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const data = await response.json();
            
            console.log('📥 Server response:', data);
            
            if (response.ok && data.success) {
                alert(editingMediaId ? 'Media updated successfully!' : 'Media uploaded successfully!');
                handleCancelMediaEdit();
                fetchMediaItems();
            } else {
                console.error('❌ Upload failed:', data);
                alert('Error: ' + (data.message || 'Upload failed. Please try again.'));
            }
        } catch (error) {
            console.error('❌ Error uploading media:', error);
            alert('Network error: ' + error.message + '. Please try again.');
        } finally {
            setMediaLoading(false);
        }
    };

    const handleEditMedia = (media) => {
        setEditingMediaId(media.id);
        setMediaTitle(media.title);
        setMediaDescription(media.description || '');
        setMediaType(media.type);
        setMediaPreview(`https://tstranscareers.in/api/${media.file_path}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteMedia = async (id) => {
        if (!window.confirm('Are you sure you want to delete this media?')) {
            return;
        }

        setMediaLoading(true);
        try {
            const token = getAdminToken();
            const response = await fetch(`https://tstranscareers.in/api/api/media/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            if (data.success) {
                alert('Media deleted successfully!');
                fetchMediaItems();
            } else {
                alert('Error: ' + data.message);
            }
        } catch (error) {
            console.error('Error deleting media:', error);
            alert('Network error. Please try again.');
        } finally {
            setMediaLoading(false);
        }
    };

    const handleToggleMediaStatus = async (id) => {
        setMediaLoading(true);
        try {
            const token = getAdminToken();
            const response = await fetch(`https://tstranscareers.in/api/api/media/${id}/toggle`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            if (data.success) {
                fetchMediaItems();
            } else {
                alert('Error: ' + data.message);
            }
        } catch (error) {
            console.error('Error toggling media status:', error);
            alert('Network error. Please try again.');
        } finally {
            setMediaLoading(false);
        }
    };

    const handleCancelMediaEdit = () => {
        setEditingMediaId(null);
        setMediaTitle('');
        setMediaDescription('');
        setMediaFile(null);
        setMediaPreview(null);
        setMediaType('photo');
    };

    const handleUpdateProfile = async () => {
        if (!selectedProfile) return;
        
        const isCandidate = selectedProfile.hasOwnProperty('candidateName');
        const endpoint = isCandidate 
            ? `https://tstranscareers.in/api/admin/candidate-update/${selectedProfile.id}`
            : `https://tstranscareers.in/api/admin/employer-update/${selectedProfile.id}`;
            
        const token = getAdminToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const updateData = isCandidate 
            ? {
                candidateName: editedProfile.name,
                mobileNumber: editedProfile.mobile,
                emailId: editedProfile.email
            }
            : {
                companyName: editedProfile.name,
                mobile_number: editedProfile.mobile,
                email_id: editedProfile.email
            };

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(updateData)
            });

            if (response.status === 401) {
                clearAllSession();
                navigate('/login');
                return;
            }

            const result = await response.json();

            if (response.ok) {
                // Update the selected profile with new data
                const updatedProfile = { ...selectedProfile };
                if (isCandidate) {
                    updatedProfile.candidateName = editedProfile.name;
                    updatedProfile.mobileNumber = editedProfile.mobile;
                    updatedProfile.emailId = editedProfile.email;
                } else {
                    updatedProfile.company_name = editedProfile.name;
                    updatedProfile.mobile_number = editedProfile.mobile;
                    updatedProfile.email_id = editedProfile.email;
                }
                setSelectedProfile(updatedProfile);
                
                // Refresh the lists
                if (isCandidate) {
                    fetchCandidates();
                } else {
                    fetchEmployers();
                }
                
                setIsEditingProfile(false);
                alert('Profile updated successfully!');
            } else {
                alert(`Error: ${result.message || 'Failed to update profile'}`);
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('Network error: Failed to update profile. Please try again.');
        }
    };

    const handleSendPasswordReset = async () => {
        if (!selectedProfile || !editedProfile.email) {
            alert('Email is required to send password reset link');
            return;
        }
        
        const isCandidate = selectedProfile.hasOwnProperty('candidateName');
        const token = getAdminToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        try {
            console.log('Sending password reset request to:', 'https://tstranscareers.in/api/admin/send-password-reset');
            console.log('Request data:', {
                email: editedProfile.email,
                name: editedProfile.name,
                userType: isCandidate ? 'Candidate' : 'Employer'
            });

            const response = await fetch('https://tstranscareers.in/api/admin/send-password-reset', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    email: editedProfile.email,
                    name: editedProfile.name,
                    userType: isCandidate ? 'Candidate' : 'Employer'
                })
            });

            console.log('Response status:', response.status);

            if (response.status === 401) {
                clearAllSession();
                navigate('/login');
                return;
            }

            const result = await response.json();
            console.log('Response result:', result);

            if (response.ok) {
                const mockMessage = result.mock ? ' (Mock mode - Email service not connected)' : '';
                alert(`Password reset email sent successfully to ${editedProfile.email}!${mockMessage}`);
                handleClosePasswordResetModal();
            } else {
                alert(`Error: ${result.message || 'Failed to send password reset email'}`);
            }
        } catch (error) {
            console.error('Error sending password reset:', error);
            
            // More detailed error handling
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                alert('Connection error: Please check if the backend server is running on port 5000.');
            } else if (error.name === 'SyntaxError') {
                alert('Server response error: Invalid response format.');
            } else {
                alert(`Network error: ${error.message}. Please try again.`);
            }
        }
    };

    // Status color helper
    const getStatusClass = (status) => {
      if (status === 'approved') return 'status-approved';
      if (status === 'pending') return 'status-pending';
      if (status === 'denied') return 'status-denied';
      return '';
    };

    // Candidate Table Columns (as per your first image)
    const candidateTableColumns = [
      { key: 'candidateName', label: 'Name' },
      { key: 'dateOfBirth', label: 'Date of Birth' },
      { key: 'gender', label: 'Gender' },
      { key: 'bloodGroup', label: 'Blood Group' },
      { key: 'maritalStatus', label: 'Marital Status' },
      { key: 'officialEmail', label: 'Official Email ID' },
      { key: 'personalEmail', label: 'Personal Email ID' },
      { key: 'phoneNumber', label: 'Phone Number' },
      { key: 'alternatePhone', label: 'Alternate Phone Number' },
      { key: 'currentAddress', label: 'Current Address' },
      { key: 'permanentAddress', label: 'Permanent Address' },
    ];

    // Render candidate table
    const renderCandidateTable = () => (
      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th>Status</th>
              {candidateTableColumns.map(col => (
                <th key={col.key}>{col.label}</th>
              ))}
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((row, idx) => (
              <tr key={idx}>
                <td>
                                    <span className={`status-badge ${getStatusClass(row.approval_status)}`}>
                                        {(row.approval_status || '').charAt(0).toUpperCase() + (row.approval_status || '').slice(1)}
                                    </span>
                </td>
                {candidateTableColumns.map(col => (
                  <td key={col.key}>
                    {col.key === 'dateOfBirth' ? formatDateOnly(row[col.key]) : (row[col.key] || '-')}
                  </td>
                ))}
                <td>
                  <button className="btn view-btn" onClick={() => setSelectedCandidate(row)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    // Render candidate profile details (sectioned, like your second image)
    const renderCandidateProfile = (profile) => (
      <div className="profile-detail-section">
        <button className="btn back-btn" onClick={() => setSelectedCandidate(null)}>Back</button>
        <h3>Personal Info</h3>
        <div className="profile-section-grid">
          <div><strong>Name</strong><br />{profile.candidateName}</div>
          <div><strong>Date of Birth</strong><br />{formatDateOnly(profile.dateOfBirth)}</div>
          <div><strong>Gender</strong><br />{profile.gender}</div>
          <div><strong>Blood Group</strong><br />{profile.bloodGroup}</div>
          <div><strong>Marital Status</strong><br />{profile.maritalStatus}</div>
        </div>
        <h3>Contact Info</h3>
        <div className="profile-section-grid">
          <div><strong>Official Email ID</strong><br />{profile.officialEmail}</div>
          <div><strong>Personal Email ID</strong><br />{profile.personalEmail}</div>
          <div><strong>Phone Number</strong><br />{profile.phoneNumber}</div>
          <div><strong>Alternate Phone Number</strong><br />{profile.alternatePhone}</div>
        </div>
        <h3>Addresses</h3>
        <div className="profile-section-grid">
          <div><strong>Current Address</strong><br />{profile.currentAddress}</div>
          <div><strong>Permanent Address</strong><br />{profile.permanentAddress}</div>
        </div>
      </div>
    );

    const renderProfileCard = (profile) => {
        const getStatusColor = (status) => {
            switch(status) {
                case 'approved': return '#059669';
                case 'pending': return '#ff8800';
                case 'denied': return '#dc2626';
                default: return '#666';
            }
        };

        const getStatusBgColor = (status) => {
            switch(status) {
                case 'approved': return 'linear-gradient(135deg, #4169E1 0%, #5a7cec 100%)';
                case 'pending': return 'linear-gradient(135deg, #4169E1 0%, #5a7cec 100%)';
                case 'denied': return 'linear-gradient(135deg, #4169E1 0%, #5a7cec 100%)';
                default: return 'linear-gradient(135deg, #4169E1 0%, #5a7cec 100%)';
            }
        };

        const isCandidate = profile.hasOwnProperty('candidateName');

        return (
            <div className="profile-card-new">
                <div className="profile-card-header" style={{
                    background: getStatusBgColor(profile.approval_status)
                }}>
                    <div className="profile-status-badge" style={{
                        backgroundColor: getStatusColor(profile.approval_status),
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        display: 'inline-block',
                        fontSize: '13px',
                        fontWeight: '600',
                        textTransform: 'capitalize'
                    }}>
                        {profile.approval_status}
                    </div>
                </div>
                <div className="profile-card-body">
                    <h3 className="profile-name">{profile.candidateName || profile.company_name}</h3>
                    <div className="profile-info-item">
                        <i className="fas fa-envelope" style={{color: '#4169E1', marginRight: '8px'}}></i>
                        <span>{profile.emailId || profile.login_email}</span>
                    </div>
                    <div className="profile-info-item">
                        <i className="fas fa-phone" style={{color: '#4169E1', marginRight: '8px'}}></i>
                        <span>{profile.mobileNumber || profile.mobile_number}</span>
                    </div>
                    <div className="profile-info-item">
                        <i className="fas fa-map-marker-alt" style={{color: '#4169E1', marginRight: '8px'}}></i>
                        <span>{profile.district}, {profile.city}</span>
                    </div>
                </div>
                <div className="profile-card-actions">
                    <button 
                        className="btn-card btn-view"
                        onClick={() => setSelectedProfile(profile)}
                    >
                        <i className="fas fa-eye"></i> View Details
                    </button>
                    {profile.approval_status !== 'approved' && (
                        <button 
                            className="btn-card btn-approve"
                            onClick={() => updateStatus(profile.id, 'approved', profile)}
                        >
                            <i className="fas fa-check"></i> Approve
                        </button>
                    )}
                    {profile.approval_status !== 'denied' && (
                        <button 
                            className="btn-card btn-deny"
                            onClick={() => updateStatus(profile.id, 'denied', profile)}
                        >
                            <i className="fas fa-times"></i> Deny
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderDetailedView = () => {
        if (!selectedProfile) return null;
        
        // Debug: Log the selected profile data
        console.log('Selected Profile Data:', selectedProfile);
        console.log('Certificate File:', selectedProfile.certificateFile);
        console.log('Resume File:', selectedProfile.resumeFile);
        
        // Determine if this is a candidate or employer profile
        const isCandidate = selectedProfile.hasOwnProperty('candidateName');
        
        const renderInfoRow = (label, value, key = null) => {
            // If there's no meaningful value, show nothing
            if (!value || value === '') return null;

            // Define which keys represent file uploads (using camelCase to match DB column names)
            const fileFields = [
                'tgIdCard', 'addressIdProof', 'certificateFile', 'resumeFile',
                'registration_certificate', 'annual_report', 
                'transgender_policy_certificate', 'company_logo'
            ];

            // If this row is a file, render a compact row with left label and right View button
            if (key && fileFields.includes(key)) {
                // Build the URL whether value is a plain filename or already a URL/path
                const fileUrl = typeof value === 'string' && value.startsWith('http')
                    ? value
                    : `https://tstranscareers.in/api/uploads/${value}`;

                return (
                    <div className="info-row doc-row" key={key || label}>
                        <label className="doc-label" title={label}>{label}:</label>
                        <div className="doc-action">
                            <button
                                className="btn view-btn"
                                aria-label={`View ${label}`}
                                onClick={() => setPreviewFile({ url: fileUrl, label })}
                            >
                                View
                            </button>
                        </div>
                    </div>
                );
            }

            // Default: simple label/value row
            return (
                <div className="info-row" key={key || label}>
                    <label>{label}:</label>
                    <span>{String(value)}</span>
                </div>
            );
        };

        return (
            <div className="detailed-view">
                <button 
                    className="btn back-btn"
                    onClick={() => setSelectedProfile(null)}
                >
                    Back to List
                </button>
                
                    <div className="profile-header-section">
                    <h2>{isCandidate ? 'Candidate Profile Details' : 'Employer Profile Details'}</h2>
                    <div className="profile-status-badge">
                        <span className={`status-badge ${getStatusClass(selectedProfile.approval_status)}`}>
                            {(selectedProfile.approval_status || '').charAt(0).toUpperCase() + (selectedProfile.approval_status || '').slice(1)}
                        </span>
                    </div>
                </div>
                
                <div className="profile-sections">
                    {isCandidate ? (
                        <>
                            {/* Candidate Personal Information */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Personal Information</h3>
                                    <div className="section-content">
                                        {renderInfoRow('Name', selectedProfile.candidateName)}
                                        {renderInfoRow('TG ID Number', selectedProfile.tgIdNumber)}
                                        {renderInfoRow('Date of Birth', formatDateOnly(selectedProfile.dateOfBirth))}
                                        {renderInfoRow('Gender', selectedProfile.gender)}
                                        {renderInfoRow('Blood Group', selectedProfile.bloodGroup)}
                                        {renderInfoRow('Marital Status', selectedProfile.maritalStatus)}
                                        {renderInfoRow('Educational Qualification', selectedProfile.educationalQualification)}
                                    </div>
                                </section>
                            </div>

                            {/* Candidate Contact Information */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Contact Information</h3>
                                    <div className="section-content">
                                        {renderInfoRow('Email ID', selectedProfile.emailId)}
                                        {renderInfoRow('Mobile Number', selectedProfile.mobileNumber)}
                                        {renderInfoRow('Alternate Mobile', selectedProfile.alternateMobile)}
                                        {renderInfoRow('Official Email', selectedProfile.officialEmail)}
                                        {renderInfoRow('Personal Email', selectedProfile.personalEmail)}
                                        {renderInfoRow('Phone Number', selectedProfile.phoneNumber)}
                                        {renderInfoRow('Alternate Phone', selectedProfile.alternatePhone)}
                                    </div>
                                </section>
                            </div>

                            {/* Candidate Address Information */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Address Information</h3>
                                    <div className="section-content">
                                        {renderInfoRow('District', selectedProfile.district)}
                                        {renderInfoRow('Mandal', selectedProfile.mandal)}
                                        {renderInfoRow('City', selectedProfile.city)}
                                        {renderInfoRow('Pincode', selectedProfile.pincode)}
                                        {renderInfoRow('Current Address', selectedProfile.currentAddress)}
                                        {renderInfoRow('Permanent Address', selectedProfile.permanentAddress)}
                                    </div>
                                </section>
                            </div>

                            {/* Candidate Professional Information */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Professional Information</h3>
                                    <div className="section-content">
                                        {renderInfoRow('Preferred Job Location', selectedProfile.preferredJobLocation)}
                                        {renderInfoRow('Preferred Job Sector', selectedProfile.preferredJobSector)}
                                        {renderInfoRow('Skills', selectedProfile.skills)}
                                        {renderInfoRow('Experience', selectedProfile.experience)}
                                        {renderInfoRow('Current Job Title', selectedProfile.currentJobTitle)}
                                        {renderInfoRow('Current Company', selectedProfile.currentCompany)}
                                    </div>
                                </section>
                            </div>

                            {/* Candidate Qualification */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Qualification</h3>
                                    <div className="section-content">
                                        {renderInfoRow('Educational Qualification', selectedProfile.educationalQualification)}
                                    </div>
                                </section>
                            </div>

                            {/* Candidate Documents */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Documents</h3>
                                    <div className="section-content">
                                        {renderInfoRow('TG ID Card', selectedProfile.tgIdCard, 'tgIdCard')}
                                        {renderInfoRow('Address ID Proof', selectedProfile.addressIdProof, 'addressIdProof')}
                                        {renderInfoRow('Certificate File', selectedProfile.certificateFile, 'certificateFile')}
                                        {renderInfoRow('Resume File', selectedProfile.resumeFile, 'resumeFile')}
                                        {renderInfoRow('Other Documents', selectedProfile.otherDocuments)}
                                    </div>
                                </section>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Employer Company Information */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Company Information</h3>
                                    <div className="section-content">
                                        {renderInfoRow('Company Name', selectedProfile.company_name)}
                                        {renderInfoRow('Brand Name', selectedProfile.brand_name)}
                                        {renderInfoRow('Sector', selectedProfile.sector)}
                                        {renderInfoRow('Website', selectedProfile.website)}
                                        {renderInfoRow('GST Number', selectedProfile.gst_number)}
                                        {renderInfoRow('Description', selectedProfile.description)}
                                        {renderInfoRow('Number of Employees', selectedProfile.number_of_employees)}
                                    </div>
                                </section>
                            </div>

                            {/* Employer Policies */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Company Policies</h3>
                                    <div className="section-content">
                                        {renderInfoRow('Has Transgender Persons Policy', selectedProfile.has_transgender_policy)}
                                        {renderInfoRow('Open to Accommodation', selectedProfile.open_to_accommodation)}
                                        {renderInfoRow('Transgender Persons Policy Details', selectedProfile.transgender_policy_details)}
                                    </div>
                                </section>
                            </div>

                            {/* Employer Contact Information */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Contact Information</h3>
                                    <div className="section-content">
                                        {renderInfoRow('Contact Person Name', selectedProfile.contact_person_name)}
                                        {renderInfoRow('Department', selectedProfile.department)}
                                        {renderInfoRow('Designation', selectedProfile.designation)}
                                        {renderInfoRow('Mobile Number', selectedProfile.mobile_number)}
                                        {renderInfoRow('Alternative Mobile', selectedProfile.alternative_mobile)}
                                        {renderInfoRow('Email ID', selectedProfile.email_id)}
                                        {renderInfoRow('Login Email', selectedProfile.login_email)}
                                    </div>
                                </section>
                            </div>

                            {/* Employer Address Information */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Address Information</h3>
                                    <div className="section-content">
                                        {renderInfoRow('District', selectedProfile.district)}
                                        {renderInfoRow('Mandal', selectedProfile.mandal)}
                                        {renderInfoRow('City', selectedProfile.city)}
                                        {renderInfoRow('Pincode', selectedProfile.pincode)}
                                        {renderInfoRow('Company Address', selectedProfile.company_address)}
                                    </div>
                                </section>
                            </div>

                            {/* Employer Documents - split into two subsections */}
                            <div className="profile-column">
                                <section className="profile-section">
                                    <h3>Company Documents</h3>
                                    <div className="section-content documents-grid">
                                        <div className="documents-column">
                                            {renderInfoRow('Registration Certificate', selectedProfile.registration_certificate, 'registration_certificate')}
                                            {renderInfoRow('Annual Report', selectedProfile.annual_report, 'annual_report')}
                                        </div>
                                        <div className="documents-column">
                                            {renderInfoRow('Transgender Persons Policy Certificate', selectedProfile.transgender_policy_certificate, 'transgender_policy_certificate')}
                                            {renderInfoRow('Company Logo', selectedProfile.company_logo, 'company_logo')}
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </>
                    )}
                </div>

                {/* Action Buttons for All Profiles */}
                <div className="profile-actions-section">
                    <h3>Profile Actions</h3>
                    <div className="action-buttons">
                        {selectedProfile.approval_status !== 'approved' && (
                            <button 
                                className="btn approve-btn"
                                onClick={() => updateStatus(selectedProfile.id, 'approved', selectedProfile)}
                            >
                                Approve Profile
                            </button>
                        )}
                        {selectedProfile.approval_status !== 'denied' && (
                            <button 
                                className="btn deny-btn"
                                onClick={() => updateStatus(selectedProfile.id, 'denied', selectedProfile)}
                            >
                                Deny Profile
                            </button>
                        )}
                        <div className="current-status">
                            <strong>Current Status:</strong> 
                            <span className={`status-badge ${getStatusClass(selectedProfile.approval_status)}`}>
                                {(selectedProfile.approval_status || 'pending').charAt(0).toUpperCase() + (selectedProfile.approval_status || 'pending').slice(1)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Password Reset section */}
                <div className="profile-actions-section">
                    <h3>Password Reset</h3>
                    <div className="action-buttons">
                        <button 
                            className="btn generate-password-btn"
                            onClick={handleGeneratePassword}
                            style={{
                                backgroundColor: '#6f42c1',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '5px',
                                cursor: 'pointer'
                            }}
                        >
                            🔑 Generate Password Reset
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const toggleMenu = (menu) => {
        setExpandedMenu(expandedMenu === menu ? null : menu);
    };

    // Toggle mobile top-nav dropdowns (used on small screens)
    const toggleMobileTopMenu = (menu) => {
        // ensure slide menu is closed when opening top dropdown
        setMobileMenuOpen(false);
        setExpandedMenu(expandedMenu === menu ? null : menu);
    };

    // Mobile menu controls
    const openMobileMenu = () => { setMobileMenuOpen(true); setExpandedMenu(null); };
    const closeMobileMenu = () => setMobileMenuOpen(false);
    const goTo = (path) => { closeMobileMenu(); navigate(path); };

    const renderMobileMenu = () => (
        <>
            <button className="mobile-slide-item" onClick={() => { setActiveTab('overview'); setStatusFilter('all'); setSelectedProfile(null); closeMobileMenu(); }}>Overview</button>

            {/* Employers accordion */}
            <div className="mobile-group">
                <button className={`mobile-group-header ${expandedMenu === 'employers' ? 'open' : ''}`} onClick={() => setExpandedMenu(expandedMenu === 'employers' ? null : 'employers')}>
                    <span>Employer Profiles</span>
                    <span className="caret">▾</span>
                </button>
                {expandedMenu === 'employers' && (
                    <div className="mobile-subitems">
                        <button className="mobile-pill" onClick={() => { setActiveTab('employers'); setStatusFilter('all'); fetchEmployers(); closeMobileMenu(); }}>All Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('employers'); setStatusFilter('pending'); fetchEmployers(); closeMobileMenu(); }}>Pending Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('employers'); setStatusFilter('approved'); fetchEmployers(); closeMobileMenu(); }}>Approved Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('employers'); setStatusFilter('denied'); fetchEmployers(); closeMobileMenu(); }}>Denied Profiles</button>
                    </div>
                )}
            </div>

            {/* Candidates accordion */}
            <div className="mobile-group">
                <button className={`mobile-group-header ${expandedMenu === 'candidates' ? 'open' : ''}`} onClick={() => setExpandedMenu(expandedMenu === 'candidates' ? null : 'candidates')}>
                    <span>Candidate Profiles</span>
                    <span className="caret">▾</span>
                </button>
                {expandedMenu === 'candidates' && (
                    <div className="mobile-subitems">
                        <button className="mobile-pill" onClick={() => { setActiveTab('candidates'); setStatusFilter('all'); fetchCandidates(); closeMobileMenu(); }}>All Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('candidates'); setStatusFilter('pending'); fetchCandidates(); closeMobileMenu(); }}>Pending Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('candidates'); setStatusFilter('approved'); fetchCandidates(); closeMobileMenu(); }}>Approved Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('candidates'); setStatusFilter('denied'); fetchCandidates(); closeMobileMenu(); }}>Denied Profiles</button>
                    </div>
                )}
            </div>

            {/* Other accordion (kept in slide-in menu) */}
            <div className="mobile-group">
                <button className={`mobile-group-header ${expandedMenu === 'other' ? 'open' : ''}`} onClick={() => setExpandedMenu(expandedMenu === 'other' ? null : 'other')}>
                    <span>Other</span>
                    <span className="caret">▾</span>
                </button>
                {expandedMenu === 'other' && (
                    <div className="mobile-subitems">
                        <button className="mobile-slide-item" onClick={() => { setActiveTab('notifications'); fetchNotifications(); closeMobileMenu(); }}>Notifications</button>
                        <button className="mobile-slide-item" onClick={() => { setActiveTab('media'); fetchMediaItems(); closeMobileMenu(); }}>Media Gallery</button>
                        <button className="mobile-slide-item" onClick={() => { handleLogout(); closeMobileMenu(); }}>Logout</button>
                    </div>
                )}
            </div>
        </>
    );

    // Database status banner component
    const DatabaseStatusBanner = () => {
        if (dbStatus === 'connected') return null;
        
        return (
            <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '16px',
                margin: '16px 0',
                color: '#92400e'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>⚠️</span>
                    <div>
                        <strong>Database Service Unavailable</strong>
                        <p style={{ margin: '4px 0 8px 0', fontSize: '14px' }}>
                            MySQL database is not connected. To view and manage profiles:
                        </p>
                        <ol style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '14px' }}>
                            <li>Open PowerShell as <strong>Administrator</strong></li>
                            <li>Run: <code style={{ background: '#f3f4f6', padding: '2px 4px', borderRadius: '4px' }}>Start-Service -Name "MySQL80"</code></li>
                            <li>Refresh this page</li>
                        </ol>
                        <button 
                            onClick={() => window.location.reload()}
                            style={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            🔄 Refresh Page
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`admin-dashboard${sidebarCollapsed ? ' collapsed' : ''}`}>
            <aside className="admin-sidebar">
                <div className="sidebar-header">
                    <button
                        className="collapse-toggle"
                        onClick={() => setSidebarCollapsed(v => !v)}
                    >
                        <span className="hamburger"><span></span><span></span><span></span></span>
                    </button>
                    <div className="brand">Admin Dashboard</div>
                </div>
                
                <nav className="sidebar-menu">
                    {/* Overview Menu */}
                    <div className="menu-group">
                        <button
                            className={`menu-item${activeTab === 'overview' ? ' expanded' : ''}`}
                            onClick={() => {
                                setActiveTab('overview');
                                setStatusFilter('all');
                                setSelectedProfile(null);
                                window.scrollTo(0,0);
                            }}
                        >
                            <span className="icon">📊</span>
                            {!sidebarCollapsed && (
                                <>
                                    <span className="label">Overview</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Employer Menu */}
                    <div className="menu-group">
                        <button
                            className={`menu-item${expandedMenu === 'employers' ? ' expanded' : ''}`}
                            onClick={() => {
                                if (sidebarCollapsed) {
                                    // If collapsed, expand sidebar and open the employers list
                                    setSidebarCollapsed(false);
                                    setActiveTab('employers');
                                    setStatusFilter('all');
                                    setSelectedProfile(null);
                                    // also expand the employers submenu
                                    setExpandedMenu('employers');
                                } else {
                                    toggleMenu('employers');
                                }
                            }}
                        >
                            <span className="icon">🏢</span>
                            {!sidebarCollapsed && (
                                <>
                                    <span className="label">Employer Profiles</span>
                                    <span className="arrow">▼</span>
                                </>
                            )}
                        </button>
                        
                        {!sidebarCollapsed && expandedMenu === 'employers' && (
                            <div className="submenu">
                                <button
                                    className={`submenu-item ${activeTab === 'employers' && statusFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('employers');
                                        setStatusFilter('all');
                                        setSelectedProfile(null);
                                        window.scrollTo(0,0);
                                    }}
                                >
                                    All Profiles
                                </button>
                                <button
                                    className={`submenu-item ${activeTab === 'employers' && statusFilter === 'pending' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('employers');
                                        setStatusFilter('pending');
                                        setSelectedProfile(null);
                                        window.scrollTo(0,0);
                                    }}
                                >
                                    Pending Profiles
                                </button>
                                <button
                                    className={`submenu-item ${activeTab === 'employers' && statusFilter === 'approved' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('employers');
                                        setStatusFilter('approved');
                                        setSelectedProfile(null);
                                        window.scrollTo(0,0);
                                    }}
                                >
                                    Approved Profiles
                                </button>
                                <button
                                    className={`submenu-item ${activeTab === 'employers' && statusFilter === 'denied' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('employers');
                                        setStatusFilter('denied');
                                        setSelectedProfile(null);
                                        window.scrollTo(0,0);
                                    }}
                                >
                                    Denied Profiles
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Candidate Menu */}
                    <div className="menu-group">
                        <button
                            className={`menu-item${expandedMenu === 'candidates' ? ' expanded' : ''}`}
                            onClick={() => {
                                if (sidebarCollapsed) {
                                    setSidebarCollapsed(false);
                                    setActiveTab('candidates');
                                    setStatusFilter('all');
                                    setSelectedProfile(null);
                                    setExpandedMenu('candidates');
                                } else {
                                    toggleMenu('candidates');
                                }
                            }}
                        >
                            <span className="icon">👤</span>
                            {!sidebarCollapsed && (
                                <>
                                    <span className="label">Candidate Profiles</span>
                                    <span className="arrow">▼</span>
                                </>
                            )}
                        </button>
                        
                        {!sidebarCollapsed && expandedMenu === 'candidates' && (
                            <div className="submenu">
                                <button
                                    className={`submenu-item${activeTab === 'candidates' && statusFilter === 'all' ? ' active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('candidates');
                                        setStatusFilter('all');
                                        setSelectedProfile(null);
                                        window.scrollTo(0,0);
                                    }}
                                >
                                    All Profiles
                                </button>
                                <button
                                    className={`submenu-item${activeTab === 'candidates' && statusFilter === 'pending' ? ' active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('candidates');
                                        setStatusFilter('pending');
                                        setSelectedProfile(null);
                                        window.scrollTo(0,0);
                                    }}
                                >
                                    Pending Profiles
                                </button>
                                <button
                                    className={`submenu-item${activeTab === 'candidates' && statusFilter === 'approved' ? ' active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('candidates');
                                        setStatusFilter('approved');
                                        setSelectedProfile(null);
                                        window.scrollTo(0,0);
                                    }}
                                >
                                    Approved Profiles
                                </button>
                                <button
                                    className={`submenu-item${activeTab === 'candidates' && statusFilter === 'denied' ? ' active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('candidates');
                                        setStatusFilter('denied');
                                        setSelectedProfile(null);
                                        window.scrollTo(0,0);
                                    }}
                                >
                                    Denied Profiles
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Notification Management Menu */}
                    <div className="menu-group">
                        <button
                            className={`menu-item${activeTab === 'notifications' ? ' expanded' : ''}`}
                            onClick={() => {
                                setActiveTab('notifications');
                                setStatusFilter('all');
                                setSelectedProfile(null);
                                fetchNotifications();
                                window.scrollTo(0,0);
                            }}
                        >
                            <span className="icon">📢</span>
                            {!sidebarCollapsed && (
                                <>
                                    <span className="label">Notifications</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Media Management Menu */}
                    <div className="menu-group">
                        <button
                            className={`menu-item${activeTab === 'media' ? ' expanded' : ''}`}
                            onClick={() => {
                                setActiveTab('media');
                                setStatusFilter('all');
                                setSelectedProfile(null);
                                fetchMediaItems();
                                window.scrollTo(0,0);
                            }}
                        >
                            <span className="icon">🎬</span>
                            {!sidebarCollapsed && (
                                <>
                                    <span className="label">Media Gallery</span>
                                </>
                            )}
                        </button>
                    </div>
                </nav>
                
                <div className="sidebar-footer">
                    <button className="logout" onClick={handleLogout}>Logout</button>
                </div>
            </aside>

            {/* Mobile top toolbar (icon-like) to match home side menu behavior on small screens */}
            <div className="mobile-top-nav" role="navigation" aria-label="Mobile Navigation">
                <button className="collapse-toggle mobile-hamburger" onClick={() => setSidebarCollapsed(v => !v)} aria-label="Toggle menu">
                    <span className="hamburger"><span></span><span></span><span></span></span>
                </button>
                <button className={`sidebar-item ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => { setActiveTab('overview'); setExpandedMenu(null); window.scrollTo(0,0); }}>
                    <span>Overview</span>
                </button>
                <button className={`sidebar-item ${expandedMenu === 'employers' ? 'active' : ''}`} onClick={() => { toggleMobileTopMenu('employers'); }}>
                    <span>Employers</span>
                </button>
                <button className={`sidebar-item ${expandedMenu === 'candidates' ? 'active' : ''}`} onClick={() => { toggleMobileTopMenu('candidates'); }}>
                    <span>Candidates</span>
                </button>
                {/* 'Other' removed from mobile top-nav as requested */}
            </div>

            {/* Mobile top dropdown (appears under top-nav on small screens) */}
            {expandedMenu && !mobileMenuOpen && (
            <div className={`mobile-top-dropdown ${expandedMenu ? 'open' : ''}`}>
                {expandedMenu === 'employers' && (
                    <div className="mobile-subitems">
                        <button className="mobile-pill" onClick={() => { setActiveTab('employers'); setStatusFilter('all'); fetchEmployers(); setExpandedMenu(null); }}>All Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('employers'); setStatusFilter('pending'); fetchEmployers(); setExpandedMenu(null); }}>Pending Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('employers'); setStatusFilter('approved'); fetchEmployers(); setExpandedMenu(null); }}>Approved Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('employers'); setStatusFilter('denied'); fetchEmployers(); setExpandedMenu(null); }}>Denied Profiles</button>
                    </div>
                )}
                {expandedMenu === 'candidates' && (
                    <div className="mobile-subitems">
                        <button className="mobile-pill" onClick={() => { setActiveTab('candidates'); setStatusFilter('all'); fetchCandidates(); setExpandedMenu(null); }}>All Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('candidates'); setStatusFilter('pending'); fetchCandidates(); setExpandedMenu(null); }}>Pending Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('candidates'); setStatusFilter('approved'); fetchCandidates(); setExpandedMenu(null); }}>Approved Profiles</button>
                        <button className="mobile-pill" onClick={() => { setActiveTab('candidates'); setStatusFilter('denied'); fetchCandidates(); setExpandedMenu(null); }}>Denied Profiles</button>
                    </div>
                )}
                {/* 'Other' dropdown removed from mobile top-dropdown */}
            </div>
            )}

            {/* Mobile slide menu/backdrop (renders sidebar items on mobile) */}
            <div className={"mobile-menu-backdrop " + (mobileMenuOpen ? 'visible' : '')} onClick={closeMobileMenu} />
            <aside className={"mobile-slide-menu " + (mobileMenuOpen ? 'open' : '')} role="dialog" aria-modal={mobileMenuOpen}>
                <button className="mobile-menu-close" onClick={closeMobileMenu} aria-label="Close menu">×</button>
                <div className="mobile-slide-content">{renderMobileMenu()}</div>
            </aside>

            <button className="mobile-fab" onClick={openMobileMenu} aria-label="Open menu">
                <div className="hamburger"><span></span><span></span><span></span></div>
            </button>

            <div className="admin-content">
                <DatabaseStatusBanner />
                {showRemarkModal && (
                    <div className="modal-backdrop" role="dialog" aria-modal="true">
                        <div className="modal-card">
                            <div className="modal-header">Enter Rejection Remark</div>
                            <div className="modal-body">
                                <label htmlFor="remark-textarea" style={{ display: 'block', marginBottom: 6 }}>Remark (required)</label>
                                <textarea
                                    id="remark-textarea"
                                    className="remark-textarea"
                                    value={remarkText}
                                    onChange={(e) => setRemarkText(e.target.value)}
                                    placeholder="Provide a brief reason for denial"
                                />
                            </div>
                            <div className="modal-footer">
                                <button className="btn secondary" onClick={() => { setShowRemarkModal(false); setRemarkText(''); setRemarkContext(null); }}>Cancel</button>
                                <button className="btn deny-btn" onClick={handleSubmitDenialRemark}>Deny Profile</button>
                            </div>
                        </div>
                    </div>
                )}
                {/* Overview */}
                {!selectedProfile && activeTab === 'overview' && (
                    <div className="overview">
                        <div className="overview-card">
                            <div className="overview-title">Candidate Profiles</div>
                            <div className="overview-value">{candidateCounts.total}</div>
                            <div className="overview-subgrid">
                                <div className="chip"><span className="label">Approved</span><span className="num">{candidateCounts.approved}</span></div>
                                <div className="chip"><span className="label">Denied</span><span className="num">{candidateCounts.denied}</span></div>
                                <div className="chip"><span className="label">Pending</span><span className="num">{candidateCounts.pending}</span></div>
                            </div>
                        </div>
                        <div className="overview-card">
                            <div className="overview-title">Employer Profiles</div>
                            <div className="overview-value">{employerCounts.total}</div>
                            <div className="overview-subgrid">
                                <div className="chip"><span className="label">Approved</span><span className="num">{employerCounts.approved}</span></div>
                                <div className="chip"><span className="label">Denied</span><span className="num">{employerCounts.denied}</span></div>
                                <div className="chip"><span className="label">Pending</span><span className="num">{employerCounts.pending}</span></div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Notification Management */}
                {activeTab === 'notifications' && !selectedProfile && (
                    <section className="notifications-management">
                        <h2>📢 Notification Banner Management</h2>
                        <p className="section-description">
                            Create and manage notification banners that appear at the top of the website header. 
                            Up to 6 notifications can be active simultaneously in the scrolling ticker.
                        </p>
                        <div className="active-notification-counter">
                            <span className="counter-label">Active Notifications:</span>
                            <span className={`counter-value ${notifications.filter(n => n.is_active).length >= 6 ? 'max-reached' : ''}`}>
                                {notifications.filter(n => n.is_active).length} / 6
                            </span>
                        </div>

                        <div className="notification-form-card">
                            <h3>{editingNotificationId ? '✏️ Edit Notification' : '➕ Create New Notification'}</h3>
                            <div className="form-group">
                                <label htmlFor="notification-message">Notification Message</label>
                                <input
                                    type="text"
                                    id="notification-message"
                                    value={notificationMessage}
                                    onChange={(e) => setNotificationMessage(e.target.value)}
                                    placeholder="Enter notification message... (e.g., 'New job postings available! Apply now for government positions.')"
                                    className="notification-input"
                                />
                            </div>
                            <div className="form-actions">
                                {editingNotificationId ? (
                                    <>
                                        <button 
                                            onClick={() => handleUpdateNotification(editingNotificationId)}
                                            className="btn btn-primary"
                                            disabled={notificationLoading}
                                        >
                                            {notificationLoading ? 'Updating...' : 'Update Notification'}
                                        </button>
                                        <button 
                                            onClick={handleCancelEdit_Notification}
                                            className="btn btn-secondary"
                                            disabled={notificationLoading}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <button 
                                        onClick={handleCreateNotification}
                                        className="btn btn-success"
                                        disabled={notificationLoading}
                                    >
                                        {notificationLoading ? 'Creating...' : 'Create Notification'}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="notifications-list">
                            <h3>📋 All Notifications</h3>
                            {notifications.length === 0 ? (
                                <div className="empty-state">
                                    <p>No notifications yet. Create your first notification above!</p>
                                </div>
                            ) : (
                                <div className="notification-cards">
                                    {notifications.map((notification) => (
                                        <div 
                                            key={notification.id} 
                                            className={`notification-card ${notification.is_active ? 'active' : 'inactive'}`}
                                        >
                                            <span className={`status-indicator ${notification.is_active ? 'status-active' : 'status-inactive'}`}>
                                                {notification.is_active ? '●' : '○'}
                                            </span>
                                            <div className="notification-message">
                                                {notification.message}
                                            </div>
                                            <div className="notification-actions">
                                                <button
                                                    onClick={() => handleToggleNotification(notification.id)}
                                                    className={`btn-icon ${notification.is_active ? 'btn-toggle-off' : 'btn-toggle-on'}`}
                                                    disabled={notificationLoading}
                                                    title={notification.is_active ? 'Deactivate' : 'Activate'}
                                                >
                                                    {notification.is_active ? '⏸' : '▶'}
                                                </button>
                                                <button
                                                    onClick={() => handleEditNotification(notification)}
                                                    className="btn-icon btn-edit"
                                                    disabled={notificationLoading}
                                                    title="Edit"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteNotification(notification.id)}
                                                    className="btn-icon btn-delete"
                                                    disabled={notificationLoading}
                                                    title="Delete"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* Media Management Section */}
                {activeTab === 'media' && !selectedProfile && (
                    <section className="media-management">
                        <h2>🎬 Media Gallery Management</h2>
                        <p className="section-description">Manage photos and videos for the "Our Department" section on the home page</p>

                        <div className="media-form-card">
                            <h3>➕ Add New Media</h3>
                            
                            <div className="form-group">
                                <label htmlFor="media-type">Media Type</label>
                                <select
                                    id="media-type"
                                    value={mediaType}
                                    onChange={(e) => {
                                        setMediaType(e.target.value);
                                        setMediaFile(null);
                                        setMediaPreview(null);
                                    }}
                                    className="form-select"
                                >
                                    <option value="photo">📷 Photo</option>
                                    <option value="video">🎥 Video</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="media-file">
                                    {mediaType === 'photo' ? 'Photo File' : 'Video File'} *
                                    <span className="file-size-hint">
                                        (Max: {mediaType === 'photo' ? '2MB' : '3MB'})
                                    </span>
                                </label>
                                <input
                                    type="file"
                                    id="media-file"
                                    accept={mediaType === 'photo' ? 'image/*' : 'video/*'}
                                    onChange={handleMediaFileChange}
                                    className="form-file-input"
                                />
                                {mediaFile && (
                                    <div className="file-info">
                                        📎 {mediaFile.name} ({(mediaFile.size / (1024 * 1024)).toFixed(2)}MB)
                                    </div>
                                )}
                                {mediaPreview && (
                                    <div className="media-preview">
                                        {mediaType === 'photo' ? (
                                            <img src={mediaPreview} alt="Preview" />
                                        ) : (
                                            <video src={mediaPreview} controls />
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="form-actions">
                                <button
                                    onClick={handleCreateMedia}
                                    className="btn btn-primary"
                                    disabled={mediaLoading}
                                >
                                    {mediaLoading ? 'Processing...' : 'Upload Media'}
                                </button>
                            </div>
                        </div>

                        <div className="media-list">
                            <h3>📋 All Media Items ({mediaItems.length})</h3>

                            {/* Media view selector */}
                            <div className="media-view-toggle" style={{ margin: '12px 0' }}>
                                <label style={{ marginRight: 8 }}>Show:</label>
                                <select value={mediaView} onChange={(e) => setMediaView(e.target.value)}>
                                    <option value="images">Images</option>
                                    <option value="videos">Videos</option>
                                </select>
                            </div>

                            {mediaItems.length === 0 ? (
                                <div className="empty-state">
                                    <p>No media items yet. Upload your first photo or video above!</p>
                                </div>
                            ) : (
                                <>
                                    {/* Images view */}
                                    {mediaView === 'images' && (
                                        <div className="media-section">
                                            <h4>🖼️ Image Items ({filteredPhotoItems.length})</h4>

                                            <div className="filters media-filters">
                                                <input
                                                    type="text"
                                                    placeholder="Search title..."
                                                    value={photoFilter.title}
                                                    onChange={(e) => setPhotoFilter({ ...photoFilter, title: e.target.value })}
                                                    className="filter-input"
                                                />
                                                <select
                                                    value={photoFilter.status}
                                                    onChange={(e) => setPhotoFilter({ ...photoFilter, status: e.target.value })}
                                                    className="filter-select"
                                                >
                                                    <option value="all">All</option>
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                                <button className="btn clear" onClick={() => setPhotoFilter({ title: '', status: 'all' })}>Clear</button>
                                            </div>

                                            {filteredPhotoItems.length === 0 ? (
                                                <div className="empty-state"><p>No images available.</p></div>
                                            ) : (
                                                <>
                                                    <div className="media-grid">
                                                        {filteredPhotoItems
                                                            .slice(
                                                                (currentPhotoPage - 1) * mediaItemsPerPage,
                                                                currentPhotoPage * mediaItemsPerPage
                                                            )
                                                            .map((media) => (
                                                            <div 
                                                                key={media.id} 
                                                                className={`media-card ${media.is_active ? 'active' : 'inactive'}`}
                                                            >
                                                                <div className="media-thumbnail">
                                                                    <img 
                                                                        src={`https://tstranscareers.in/api/${media.file_path}`} 
                                                                        alt={media.title}
                                                                        loading="lazy"
                                                                    />
                                                                    <span className="media-type-badge">📷</span>
                                                                </div>
                                                                <div className="media-info">
                                                                    <div className="media-meta">
                                                                        <span className={`status-badge ${media.is_active ? 'active' : 'inactive'}`}>
                                                                            {media.is_active ? '● Active' : '○ Inactive'}
                                                                        </span>
                                                                        <span className="media-date">{new Date(media.created_at).toLocaleDateString()}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="media-actions">
                                                                    <button
                                                                        onClick={() => handleToggleMediaStatus(media.id)}
                                                                        className={`btn-icon ${media.is_active ? 'btn-toggle-off' : 'btn-toggle-on'}`}
                                                                        disabled={mediaLoading}
                                                                        title={media.is_active ? 'Deactivate' : 'Activate'}
                                                                    >{media.is_active ? '⏸' : '▶'}</button>
                                                                    <button
                                                                        onClick={() => handleDeleteMedia(media.id)}
                                                                        className="btn-icon btn-delete"
                                                                        disabled={mediaLoading}
                                                                        title="Delete"
                                                                    >🗑️</button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {filteredPhotoItems.length > mediaItemsPerPage && (
                                                        <div className="pagination">
                                                            <button
                                                                onClick={() => setCurrentPhotoPage(prev => Math.max(1, prev - 1))}
                                                                disabled={currentPhotoPage === 1}
                                                                className="pagination-btn"
                                                            >← Previous</button>
                                                            <span className="pagination-info">Page {currentPhotoPage} of {Math.ceil(filteredPhotoItems.length / mediaItemsPerPage)}</span>
                                                            <button
                                                                onClick={() => setCurrentPhotoPage(prev => Math.min(Math.ceil(filteredPhotoItems.length / mediaItemsPerPage), prev + 1))}
                                                                disabled={currentPhotoPage >= Math.ceil(filteredPhotoItems.length / mediaItemsPerPage)}
                                                                className="pagination-btn"
                                                            >Next →</button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* Videos view */}
                                    {mediaView === 'videos' && (
                                        <div className="media-section">
                                            <h4>🎥 Video Items ({filteredVideoItems.length})</h4>

                                            <div className="filters media-filters">
                                                <input
                                                    type="text"
                                                    placeholder="Search title..."
                                                    value={videoFilter.title}
                                                    onChange={(e) => setVideoFilter({ ...videoFilter, title: e.target.value })}
                                                    className="filter-input"
                                                />
                                                <select
                                                    value={videoFilter.status}
                                                    onChange={(e) => setVideoFilter({ ...videoFilter, status: e.target.value })}
                                                    className="filter-select"
                                                >
                                                    <option value="all">All</option>
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                                <button className="btn clear" onClick={() => setVideoFilter({ title: '', status: 'all' })}>Clear</button>
                                            </div>

                                            {filteredVideoItems.length === 0 ? (
                                                <div className="empty-state"><p>No videos available.</p></div>
                                            ) : (
                                                <>
                                                    <div className="media-grid">
                                                        {filteredVideoItems
                                                            .slice(
                                                                (currentVideoPage - 1) * mediaItemsPerPage,
                                                                currentVideoPage * mediaItemsPerPage
                                                            )
                                                            .map((media) => (
                                                            <div 
                                                                key={media.id} 
                                                                className={`media-card ${media.is_active ? 'active' : 'inactive'}`}
                                                            >
                                                                <div className="media-thumbnail">
                                                                    <video 
                                                                        src={`https://tstranscareers.in/api/${media.file_path}`}
                                                                        controls={false}
                                                                    />
                                                                    <span className="media-type-badge">🎥</span>
                                                                </div>
                                                                <div className="media-info">
                                                                    <div className="media-meta">
                                                                        <span className={`status-badge ${media.is_active ? 'active' : 'inactive'}`}>
                                                                            {media.is_active ? '● Active' : '○ Inactive'}
                                                                        </span>
                                                                        <span className="media-date">{new Date(media.created_at).toLocaleDateString()}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="media-actions">
                                                                    <button
                                                                        onClick={() => handleToggleMediaStatus(media.id)}
                                                                        className={`btn-icon ${media.is_active ? 'btn-toggle-off' : 'btn-toggle-on'}`}
                                                                        disabled={mediaLoading}
                                                                        title={media.is_active ? 'Deactivate' : 'Activate'}
                                                                    >{media.is_active ? '⏸' : '▶'}</button>
                                                                    <button
                                                                        onClick={() => handleDeleteMedia(media.id)}
                                                                        className="btn-icon btn-delete"
                                                                        disabled={mediaLoading}
                                                                        title="Delete"
                                                                    >🗑️</button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {filteredVideoItems.length > mediaItemsPerPage && (
                                                        <div className="pagination">
                                                            <button
                                                                onClick={() => setCurrentVideoPage(prev => Math.max(1, prev - 1))}
                                                                disabled={currentVideoPage === 1}
                                                                className="pagination-btn"
                                                            >← Previous</button>
                                                            <span className="pagination-info">Page {currentVideoPage} of {Math.ceil(filteredVideoItems.length / mediaItemsPerPage)}</span>
                                                            <button
                                                                onClick={() => setCurrentVideoPage(prev => Math.min(Math.ceil(filteredVideoItems.length / mediaItemsPerPage), prev + 1))}
                                                                disabled={currentVideoPage >= Math.ceil(filteredVideoItems.length / mediaItemsPerPage)}
                                                                className="pagination-btn"
                                                            >Next →</button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </section>
                )}

                {previewFile ? (
                    <div className="file-preview">
                        <button className="btn back-btn" onClick={() => setPreviewFile(null)}>Back</button>
                        <h3>{previewFile.label}</h3>
                        <div className="file-content">
                            {
                                previewFile.url.match(/\.(jpg|jpeg|png|gif)$/i)
                                    ? <img src={previewFile.url} alt={previewFile.label} />
                                    : <iframe src={previewFile.url} title={previewFile.label} />
                            }
                        </div>
                    </div>
                ) : (
                    <>
                        {activeTab === 'employers' && (
                            <section>
                                <h2>{t('EmployerProfiles')}</h2>

                                {/* Employer Filters - Only show when not viewing individual profile */}
                                {!selectedProfile && (
                                    <div className="filters">
                                    <input 
                                        type="text" 
                                        placeholder="Company Name"
                                        value={employerFilters.companyName}
                                        onChange={e => setEmployerFilters({ ...employerFilters, companyName: e.target.value })}
                                        className="filter-input"
                                    />
                                    <input 
                                        type="text" 
                                        placeholder="Place"
                                        value={employerFilters.place}
                                        onChange={e => setEmployerFilters({ ...employerFilters, place: e.target.value })}
                                        className="filter-input"
                                    />
                                    <select
                                        value={employerFilters.customRange}
                                        onChange={e => setEmployerFilters({ 
                                            ...employerFilters, 
                                            customRange: e.target.value,
                                            fromDate: '', 
                                            toDate: ''  // Reset custom dates when changing range
                                        })}
                                        className="filter-select"
                                    >
                                        {DATE_RANGE_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    {employerFilters.customRange === 'custom' && (
                                        <div className="custom-date-range">
                                            <input
                                                type="date"
                                                value={employerFilters.fromDate || ''}
                                                onChange={e => setEmployerFilters({ 
                                                    ...employerFilters, 
                                                    fromDate: e.target.value 
                                                })}
                                                className="filter-input"
                                                placeholder="From Date"
                                            />
                                            <input
                                                type="date"
                                                value={employerFilters.toDate || ''}
                                                onChange={e => setEmployerFilters({ 
                                                    ...employerFilters, 
                                                    toDate: e.target.value 
                                                })}
                                                className="filter-input"
                                                placeholder="To Date"
                                            />
                                        </div>
                                    )}
                                    <button className="btn" onClick={handleEmployerSearch}>Search</button>
                                    <button 
                                        className="btn clear" 
                                        onClick={() => { 
                                            setEmployerFilters({ 
                                                companyName: '', 
                                                place: '', 
                                                customRange: 'all',
                                                fromDate: '', 
                                                toDate: '' 
                                            }); 
                                            setIsEmployerSearchApplied(false);
                                            fetchEmployers(false); // Pass false to indicate no search filters
                                        }}
                                    >
                                        Clear
                                    </button>
                                </div>
                                )}

                                {/* New employer cards grid */}
                                {selectedProfile ? (
                                    renderDetailedView()
                                ) : (
                                    <>
                                        <div className="profiles-grid">
                                            {pagedEmployers
                                                .map((profile, index) => (
                                                    <div key={`employer-${profile.id || index}`}>
                                                        {renderProfileCard(profile)}
                                                    </div>
                                                ))}
                                        </div>
                                        
                                        {/* Employer Pagination */}
                                        <PageSizeAndPagination 
                                            page={employerPage}
                                            totalPages={totalEmployerPages}
                                            onPageChange={setEmployerPage}
                                        />
                                    </>
                                )}
                            </section>
                        )}

                        {activeTab === 'candidates' && (
                            <section>
                                <h2>{t('CandidateProfiles')}</h2>

                                {/* Candidate Filters - Only show when not viewing individual profile */}
                                {!selectedProfile && (
                                    <div className="filters">
                                    <input 
                                        type="text" 
                                        placeholder="Candidate Name"
                                        value={candidateFilters.candidateName}
                                        onChange={e => setCandidateFilters({ ...candidateFilters, candidateName: e.target.value })}
                                        className="filter-input"
                                    />
                                    <input 
                                        type="text" 
                                        placeholder="TG ID Number"
                                        value={candidateFilters.tgIdNumber}
                                        onChange={e => setCandidateFilters({ ...candidateFilters, tgIdNumber: e.target.value })}
                                        className="filter-input"
                                    />
                                    <input 
                                        type="text" 
                                        placeholder="Place"
                                        value={candidateFilters.place}
                                        onChange={e => setCandidateFilters({ ...candidateFilters, place: e.target.value })}
                                        className="filter-input"
                                    />
                                    <select
                                        value={candidateFilters.customRange}
                                        onChange={e => setCandidateFilters({ 
                                            ...candidateFilters, 
                                            customRange: e.target.value,
                                            fromDate: '', 
                                            toDate: ''  // Reset custom dates when changing range
                                        })}
                                        className="filter-select"
                                    >
                                        {DATE_RANGE_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    {candidateFilters.customRange === 'custom' && (
                                        <div className="custom-date-range">
                                            <input
                                                type="date"
                                                value={candidateFilters.fromDate || ''}
                                                onChange={e => setCandidateFilters({ 
                                                    ...candidateFilters, 
                                                    fromDate: e.target.value 
                                                })}
                                                className="filter-input"
                                                placeholder="From Date"
                                            />
                                            <input
                                                type="date"
                                                value={candidateFilters.toDate || ''}
                                                onChange={e => setCandidateFilters({ 
                                                    ...candidateFilters, 
                                                    toDate: e.target.value 
                                                })}
                                                className="filter-input"
                                                placeholder="To Date"
                                            />
                                        </div>
                                    )}
                                    <button className="btn" onClick={handleCandidateSearch}>Search</button>
                                    <button 
                                        className="btn clear" 
                                        onClick={handleCandidateClear}
                                    >
                                        Clear
                                    </button>
                                </div>
                                )}

                                {/* New candidate cards grid */}
                                {selectedProfile ? (
                                    renderDetailedView()
                                ) : (
                                    <>
                                        <div className="profiles-grid">
                                            {pagedCandidates
                                                .map((profile, index) => (
                                                    <div key={`candidate-${profile.id || index}`}>
                                                        {renderProfileCard(profile)}
                                                    </div>
                                                ))}
                                        </div>
                                        
                                        {/* Candidate Pagination */}
                                        <PageSizeAndPagination 
                                            page={candidatePage}
                                            totalPages={totalCandidatePages}
                                            onPageChange={setCandidatePage}
                                        />
                                    </>
                                )}
                            </section>
                        )}
                    </>
                )}

                {/* Password Reset Modal */}
                {showPasswordResetModal && (
                    <div className="modal-backdrop password-reset-modal" onClick={handleClosePasswordResetModal}>
                        <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>🔑 Password Reset</h3>
                                <button 
                                    className="modal-close-btn"
                                    onClick={handleClosePasswordResetModal}
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="modal-body">
                                <h4>Profile Details for Password Reset</h4>
                                <div className="form-fields">
                                    <div className="field-group">
                                        <label><strong>Name:</strong></label>
                                        {isEditingProfile ? (
                                            <input
                                                type="text"
                                                value={editedProfile.name}
                                                onChange={(e) => setEditedProfile({...editedProfile, name: e.target.value})}
                                                className="edit-input"
                                            />
                                        ) : (
                                            <span className="field-value">{editedProfile.name}</span>
                                        )}
                                    </div>
                                    
                                    <div className="field-group">
                                        <label><strong>Mobile:</strong></label>
                                        {isEditingProfile ? (
                                            <input
                                                type="text"
                                                value={editedProfile.mobile}
                                                onChange={(e) => setEditedProfile({...editedProfile, mobile: e.target.value})}
                                                className="edit-input"
                                            />
                                        ) : (
                                            <span className="field-value">{editedProfile.mobile}</span>
                                        )}
                                    </div>
                                    
                                    <div className="field-group">
                                        <label><strong>Email:</strong></label>
                                        {isEditingProfile ? (
                                            <input
                                                type="email"
                                                value={editedProfile.email}
                                                onChange={(e) => setEditedProfile({...editedProfile, email: e.target.value})}
                                                className="edit-input"
                                            />
                                        ) : (
                                            <span className="field-value">{editedProfile.email}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                {!isEditingProfile ? (
                                    <>
                                        <button 
                                            className="btn edit-btn"
                                            onClick={handleEditProfile}
                                        >
                                            ✏️ Edit Details
                                        </button>
                                        <button 
                                            className="btn send-reset-btn"
                                            onClick={handleSendPasswordReset}
                                        >
                                            📧 Send Reset Email
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button 
                                            className="btn update-btn"
                                            onClick={handleUpdateProfile}
                                        >
                                            💾 Update Profile
                                        </button>
                                        <button 
                                            className="btn cancel-btn"
                                            onClick={handleCancelEdit}
                                        >
                                            ❌ Cancel
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminDashboard;